/** Chart screenshots for TES (`POST /coach/turn` `images`): up to 2, PNG / JPEG / WebP, 4 MB in all. */
export const STRATEGY_RD_COACH_MAX_IMAGES = 2;
export const STRATEGY_RD_COACH_MAX_IMAGE_BYTES = 4 * 1024 * 1024;
export const STRATEGY_RD_COACH_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export type StrategyRdCoachAttachment = {
  id: string;
  mime_type: string;
  data_base64: string;
  /** Data URL for the thumbnail. */
  preview: string;
  bytes: number;
};

/**
 * Which of `incoming` (type + size) fit next to `existing`; the rest get one plain reason.
 */
export function strategyRdCoachImagesThatFit(
  existing: ReadonlyArray<{ bytes: number }>,
  incoming: ReadonlyArray<{ type: string; size: number }>
): { accepted: number[]; error: string | null } {
  const accepted: number[] = [];
  let error: string | null = null;
  let count = existing.length;
  let bytes = existing.reduce((s, a) => s + a.bytes, 0);
  incoming.forEach((file, i) => {
    if (!(STRATEGY_RD_COACH_IMAGE_TYPES as readonly string[]).includes(file.type)) {
      error ??= "Attach PNG, JPEG or WebP screenshots.";
      return;
    }
    if (count >= STRATEGY_RD_COACH_MAX_IMAGES) {
      error ??= `Up to ${STRATEGY_RD_COACH_MAX_IMAGES} images per message.`;
      return;
    }
    if (bytes + file.size > STRATEGY_RD_COACH_MAX_IMAGE_BYTES) {
      error ??= "Images can be 4 MB in total.";
      return;
    }
    count += 1;
    bytes += file.size;
    accepted.push(i);
  });
  return { accepted, error };
}

export function readStrategyRdCoachAttachment(file: File): Promise<StrategyRdCoachAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the image."));
    reader.onload = () => {
      const url = String(reader.result || "");
      const comma = url.indexOf(",");
      resolve({
        id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        mime_type: file.type,
        data_base64: comma >= 0 ? url.slice(comma + 1) : url,
        preview: url,
        bytes: file.size,
      });
    };
    reader.readAsDataURL(file);
  });
}
